import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { db } from "../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import TablePagination from "@mui/material/TablePagination";

const CSV = () => {
  const [phoneData, setPhoneData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch data from Firestore
  useEffect(() => {
    const fetchPhoneData = async () => {
      try {
        const collectionRef = collection(db, "all-products");
        const snapshot = await getDocs(collectionRef);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filteredData = data.filter(
          (item) =>
            item.history &&
            item.history.length === 1 &&
            item.history[0].status === "inventory"
        );

        const sortedData = filteredData.sort((a, b) => {
          const timeA = a.addedOn?.seconds || 0;
          const timeB = b.addedOn?.seconds || 0;
          return timeB - timeA;
        });

        setPhoneData(sortedData);
        setFilteredData(sortedData);
      } catch (error) {
        console.error("Error fetching phone data:", error);
      }
    };

    fetchPhoneData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...phoneData];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter((item) => {
        if (!item.addedOn) return false;
        const addedDate = item.addedOn.seconds
          ? new Date(item.addedOn.seconds * 1000)
          : new Date(item.addedOn);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          return addedDate >= start && addedDate <= end;
        } else if (start) {
          return addedDate >= start;
        } else if (end) {
          return addedDate <= end;
        }
        return true;
      });
    }

    setFilteredData(filtered);
    setCurrentPage(0); // Reset to first page when filters change
  }, [searchQuery, startDate, endDate, phoneData]);

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((phone) => phone.id));
    }
  };

  const exportToExcel = () => {
    if (selectedRows.length === 0) {
      alert("No rows selected. Please select rows to export.");
      return;
    }

    const selectedData = filteredData
      .filter((phone) => selectedRows.includes(phone.id))
      .map((phone) => {
        const { variant, color } = separateVariantAndColor(phone.variant);
        return {
          Model: phone.model + " (" + variant + ")" || "N/A",
          Color: "Colour: " + color || "N/A",
          "Serial Number": phone.serialNumber || "N/A",
          IMEI: phone.imei_1 || "N/A",
          "MRP (₹)": "MRP: " + phone.mrp ? phone.mrp.toLocaleString() : "N/A",
        };
      });

    const worksheet = XLSX.utils.json_to_sheet(selectedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Phones");
    XLSX.writeFile(workbook, "selected_phones.xlsx");
  };

  const calculateTimeDifference = (timestamp) => {
    if (!timestamp) return "N/A";
    const now = new Date();
    const phoneTime = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    const timeDiff = now - phoneTime;
    const minutesDiff = Math.floor(timeDiff / (1000 * 60));

    if (minutesDiff > 1440) {
      return phoneTime.toLocaleDateString();
    }

    return `${minutesDiff} minutes ago`;
  };

  const separateVariantAndColor = (phoneDescription) => {
    if (!phoneDescription) return { variant: null, color: null };

    const parts = phoneDescription.split("/");
    const color = parts.pop().trim();
    const variant = parts.join("/").trim();

    return { variant, color };
  };

  // Paginate filtered data
  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return (
    <div className="bg-white h-screen w-full p-5 position-absolute overflow-x-auto overflow-y-auto">
      <h1 className="text-xl font-bold mb-4">Phone Details Table</h1>
      <div className="mb-4 flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded w-64"
        />
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded"
          />
          <span>to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded"
          /> 
        </div>
        <button
          onClick={toggleSelectAll}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 w-40"
        >
          {selectedRows.length === filteredData.length ? "Deselect All" : "Select All"}
        </button>
        <button
          onClick={exportToExcel}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Export Selected to Excel
        </button>
      </div>
      <table className="table-auto w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-300 p-2">Select</th>
            <th className="border border-gray-300 p-2">Brand</th>
            <th className="border border-gray-300 p-2">Model</th>
            <th className="border border-gray-300 p-2">Variant</th>
            <th className="border border-gray-300 p-2">Color</th>
            <th className="border border-gray-300 p-2">Serial Number</th>
            <th className="border border-gray-300 p-2">IMEI</th>
            <th className="border border-gray-300 p-2">Added (Time Ago)</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((phone) => {
            const { variant, color } = separateVariantAndColor(phone.variant);

            return (
              <tr
                key={phone.id}
                className={`text-center ${
                  selectedRows.includes(phone.id) ? "bg-blue-100" : ""
                }`}
                onClick={() => handleRowSelect(phone.id)}
              >
                <td className="border border-gray-300 p-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(phone.id)}
                    readOnly
                  />
                </td>
                <td className="border border-gray-300 p-2">{phone.brand || "N/A"}</td>
                <td className="border border-gray-300 p-2">{phone.model || "N/A"}</td>
                <td className="border border-gray-300 p-2">{variant || "N/A"}</td>
                <td className="border border-gray-300 p-2">{color || "N/A"}</td>
                <td className="border border-gray-300 p-2">{phone.serialNumber || "N/A"}</td>
                <td className="border border-gray-300 p-2">{phone.imei_1 || "N/A"}</td>
                <td className="border border-gray-300 p-2">
                  {calculateTimeDifference(phone.addedOn)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="bg-slate-200 flex justify-center items-center mt-10">
        <TablePagination
          component="div"
          count={filteredData.length}
          page={currentPage}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default CSV;